import { useRef, useState, type AnimationEvent, type CSSProperties, type ReactNode } from "react";
import "./LeafFall.css";

export interface LeafFallProps {
  /**
   * Plays the animation once on mount, and again while the pointer is over the children (a tap on touch screens
   * plays one more fall). When false, or when the user prefers reduced motion, the wrapper generates no box and no leaves render. The wrapper is always mounted so that turning the flag on never re-creates the children.
   */
  enabled: boolean;
  /**
   * The artwork the leaves fall in front of. It is never modified.
   */
  children: ReactNode;
}

type LeafConfig = {
  /** Left edge as a share of the wrapper width. */
  x: number;
  /** Leaf width as a share of the wrapper width. */
  size: number;
  fill: string;
  delay: number;
  duration: number;
  /** Mirrors the sway so the leaves do not move in lockstep. */
  direction: 1 | -1;
};

const LEAVES: LeafConfig[] = [
  { x: 0.04, size: 0.4, fill: "#C46A1D", delay: 0.2, duration: 4.2, direction: 1 },
  { x: 0.55, size: 0.32, fill: "#A9501A", delay: 0.9, duration: 3.8, direction: -1 },
  { x: 0.28, size: 0.36, fill: "#D2802A", delay: 1.7, duration: 4.4, direction: 1 },
  { x: 0.62, size: 0.28, fill: "#9C4A14", delay: 2.5, duration: 3.9, direction: -1 },
  { x: 0.12, size: 0.26, fill: "#C46A1D", delay: 3.2, duration: 4.1, direction: -1 }
];

const LEAF_OUTLINE =
  "M50,2 L55,14 L60,11 L62,26 L72,18 L70,34 L84,31 L78,44 L97,52 L80,60 L84,72 L66,68 L62,78 L53,74 L52,98 L48,98 L47,74 L38,78 L34,68 L16,72 L20,60 L3,52 L22,44 L16,31 L30,34 L28,18 L38,26 L40,11 L45,14Z";
const LEAF_VEINS = "M50 96 L50 30 M50 70 L26 54 M50 70 L74 54 M50 50 L33 38 M50 50 L67 38";

const MapleLeaf = ({ fill }: { fill: string }) => (
  <svg className="leaf-fall__sway" viewBox="0 0 100 100" focusable="false">
    <path d={LEAF_OUTLINE} fill={fill} stroke="#7A3A0E" strokeWidth="1" strokeLinejoin="round" />
    <path d={LEAF_VEINS} stroke="#7A3A0E" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.7" />
  </svg>
);

const LeafFall = ({ enabled, children }: LeafFallProps) => {
  const [run, setRun] = useState(0);
  const [playing, setPlaying] = useState(true);
  const hovering = useRef(false);
  const finished = useRef(0);

  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

  const active = enabled && !prefersReducedMotion;

  const handlePointerEnter = () => {
    hovering.current = true;
    if (!playing) {
      finished.current = 0;
      setRun((current) => current + 1);
      setPlaying(true);
    }
  };

  const handlePointerLeave = () => {
    hovering.current = false;
  };

  const handleDropEnd = (event: AnimationEvent<HTMLSpanElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    finished.current += 1;
    if (finished.current < LEAVES.length) {
      return;
    }

    finished.current = 0;
    if (hovering.current) {
      setRun((current) => current + 1);
    } else {
      setPlaying(false);
    }
  };

  return (
    <span
      className={active ? "leaf-fall" : "leaf-fall leaf-fall--inactive"}
      data-testid="leaf-fall"
      onPointerEnter={active ? handlePointerEnter : undefined}
      onPointerLeave={active ? handlePointerLeave : undefined}
    >
      {children}
      {active && playing && (
        <span key={run} className="leaf-fall__leaves" aria-hidden="true" data-testid="leaf-fall-leaves">
          {LEAVES.map(({ x, size, fill, delay, duration, direction }) => (
            <span
              key={x}
              className="leaf-fall__drop"
              data-testid="leaf-fall-leaf"
              onAnimationEnd={handleDropEnd}
              style={
                {
                  "--leaf-x": `${x * 100}%`,
                  "--leaf-size": `${size * 100}%`,
                  "--leaf-delay": `${delay}s`,
                  "--leaf-duration": `${duration}s`,
                  "--leaf-dir": direction
                } as CSSProperties
              }
            >
              <MapleLeaf fill={fill} />
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

export default LeafFall;
